import {omitProperties, onlyOwnProperties} from '@/lib/ReusableFunctions/Object';
import {mappedFields} from '@/lib/StandardFields';
import {InfoWindowContent} from '../InfoWindowContent';
import {DisplayField} from '../DisplayField';
import {getStore} from '@/services/store';
import {tableConfigs} from './grids/tableConfigs';

import type {Graphic} from '@/services/GraphicService/Graphic';
import type {BannedValue} from '@/services/GraphicService/MapLayer';
import type {ToolbarOptions} from '@/services/GraphicService';
import type {VectorTileGraphic} from '@/services/GraphicService/VectorTileGraphic/VectorTileGraphic';

export interface FieldConfig {
  alias: string;
  defaultValue: string;
  modelName: string;
  name: string;
  type: string;
}

export interface ArcGisLayerConfig {
  name: string;
  sublayerName?: string;
  idField?: string;
  idFields?: Record<string, string>;
  titleField?: string;
  titleFields?: string[] | Record<string, string>;
  fields?: FieldConfig[];
  customFieldMapping?: string;
  schemaVersion: number;
  banFields?: string[];
  banValues?: BannedValue[];
  tableConfig?: string;
}

type Feature = Record<string, unknown>;

/**
 * given a vector tile feature and layer config, creates the content data for the info window and returns an
 * InfoWindowContent instance
 * @param layer - the layer the feature belongs to
 * @param context - the context object containing the feature and layerName
 * @param graphic - **MUST** be the RESOLVED Graphic belonging to the feature
 * @return - the content data to create info window content with
 */
function createArcGisContentData(layer: ArcGisLayerConfig, context: {feature: Feature}, graphic: Graphic) {
  const feature = context.feature;
  try {
    const displayFields = getDisplayFields(layer, feature);
    const id = getUniqueLayerId(layer, feature);

    if (!id) {
      throw new Error('No ID field found for vector tile feature');
    }

    const title = getTitle(layer, feature);
    const rawId = getRawId(layer, feature);
    const toolbar = getToolbarOptions(layer, rawId, feature);
    const metadata = omitProperties(onlyOwnProperties(feature), ...layer?.banFields ?? []);

    return {
      type: 'vectorTile' as const,
      title,
      id: id as string | number,
      metadata,
      displayFields,
      host: {setHighlight: (state: boolean) => (graphic as unknown as VectorTileGraphic).setHighlight(state, feature)},
      toolbar,
    };
  } catch (error) {
    console.warn('Error creating vectorTile info window content', error);
    return null;
  }
}

/**
 * given a vector tile feature and layer config, creates an InfoWindowContent instance with the appropriate content data
 * for the info window. Returns null if content data creation fails for any reason
 */
function createVectorTileContent(layer: ArcGisLayerConfig, feature: {feature: Feature}, graphic: VectorTileGraphic) {
  const infoWindowContent = createArcGisContentData(layer, feature, graphic);
  if (infoWindowContent) {
    return new InfoWindowContent(infoWindowContent);
  }
  return null;
}

/** ---HELPER FUNCTIONS --- */

function getRawId(layer: ArcGisLayerConfig, feature: Feature) {
  const idField = layer.idFields ? layer.idFields?.[feature?.layer_type as string] : layer.idField;
  return idField ? feature?.[idField] : undefined;
}

function getUniqueLayerId(layer: ArcGisLayerConfig, feature: Feature) {
  const rawId = getRawId(layer, feature);
  if (layer?.sublayerName) {
    return `${rawId}_${layer.sublayerName}`;
  }
  return rawId;
}

function getTitle(layer: ArcGisLayerConfig, feature: Feature) {
  let title = (layer?.titleField ? feature?.[layer.titleField] as string : '') || '';
  if (layer.titleFields) {
    if (Array.isArray(layer.titleFields)) {
      layer.titleFields.forEach((field, index) => {
        if ((feature[field] as string)?.trim() === '') return;
        // Ex: 'First (Second) (Third) ...'
        title += index > 0 ? ` (${feature[field] || ''})` : (feature[field] as string) || '';
      });
    } else {
      const titleField = layer?.titleFields?.[feature?.layer_type as string];
      title = (titleField ? feature?.[titleField] as string : '') || layer.titleFields['defaultValue'];
    }
  }

  if (!title) {
    title = layer.name;
  }
  return title;
}

function getDisplayFields(layer: ArcGisLayerConfig, feature: Feature) {
  const bannedValues: BannedValue[] = layer?.banValues ?? [];
  const displayFields: DisplayField[] = [];
  const metadata = omitProperties(onlyOwnProperties(feature), ...layer?.banFields ?? []) as Record<string, unknown>;

  if (layer.customFieldMapping) {
    const mapName = layer.customFieldMapping || layer.name;
    mappedFields?.[mapName]?.forEach((field) => {
      let value: unknown = feature[field.arcGisKey as string];
      if (!value || String(value).trim() === '') return;

      // Skip fields that return unavailable information
      if (bannedValues.includes(value as BannedValue)) return;

      if (field.unit === 'date') {
        value = new Date(value as string | number).toLocaleDateString();
      }

      if (typeof value === 'string' && value.toLowerCase() === 'nan') {
        value = null;
      }

      const unit = field.formatUnit ?? (Array.isArray(field.unit) ? field.unit.join(', ') : field.unit);
      displayFields.push(new DisplayField({
        label: Array.isArray(field.label) ? field.label.join(', ') : field.label,
        unit,
        allowEdit: false,
        allowLabelEdit: false,
        allowDelete: false,
        value: value as string | number | null,
      }));
    });
  } else {
    layer.fields?.forEach((field) => {
      const key = field.name;
      const value = metadata[key];
      if (value == null || bannedValues.includes(value as BannedValue)) return;
      const label = field.alias;
      const isNumeric = typeof value === 'number';
      displayFields.push(new DisplayField({
        label,
        value: value as string | number,
        unit: isNumeric ? 'number' : 'string',
        allowEdit: false,
        allowLabelEdit: false,
        allowDelete: false,
      }));
    });
  }
  return displayFields;
}

function getToolbarOptions(layer: ArcGisLayerConfig, id: unknown, feature: Feature) {
  const toolbar: {toolbarOptions: Partial<ToolbarOptions>[][]} = {
    toolbarOptions: [],
  };

  // Use tableConfig from config if available, fallback to layer name for backward compatibility
  const gridConfigKey = layer.tableConfig || layer.name;
  const gridData = tableConfigs[gridConfigKey]?.(id as string, feature);

  if (gridData) {
    toolbar.toolbarOptions.push([
      {
        icon: 'Table',
        name: 'View Table',
        callback: () => getStore().commit('setGridData', gridData),
      },
    ]);
  }
  return toolbar;
}

// Re-exports kept for backward compatibility with existing tests and any
// remaining direct importers. New consumers should import from `./grids/...`
// or `./grids/tableConfigs` directly.
export {
  getMultifamilyMarketSubmarketGrid,
  getMarketSubmarketGrid,
  getExitCapRateGrid,
} from './grids/bigQueryGrids';
export {getOrenniaInterconnectionCostGrid} from './grids/orenniaGrid';

export {
  createVectorTileContent,
};
